import { TbCheck, TbShieldCheck, TbWorld, TbX } from "solid-icons/tb";
import { type Component, createSignal, For, Index, onCleanup, onMount, Show } from "solid-js";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import {
  AGENTS,
  BUILT_IN,
  type Logo,
  MCP_SERVERS,
  MEETING_APPS,
  MODEL_PROVIDERS,
} from "~/components/marketing/content";
import { LogoMark, LogoPill } from "~/components/marketing/LogoMark";
import { TabLink, tabHref } from "~/components/marketing/ui";
import { useBeat, useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import "./mockups/mockups.css";

type Point = { x: number; y: number };
type Size = { w: number; h: number };

// Positions in percent of the diagram box: the meeting on the left feeding
// Chief, and up to seven slots fanning out to the right on an arc that bows
// away from Chief, evenly spaced and centred on its row. Every tab fills the
// same slots, so switching tabs only swaps what sits at the ends of the curves.
const MEETING = { x: 15, y: 50 };
const CHIEF = { x: 43, y: 50 };
const SLOTS = 7;
const arc = (count: number): Point[] =>
  Array.from({ length: count }, (_, i) => {
    const y = 50 + (i - (count - 1) / 2) * (86 / 6);
    return { x: 60 + 19 * (1 - ((y - 50) / 46) ** 2), y };
  });
const ARC = arc(BUILT_IN.length);

// The story visits the tools out of arc order so consecutive comets fan out
// rather than step down the arc. Linear goes first: it is also the frame shown
// without motion.
const ORDER = [5, 0, 3, 1, 6, 4, 2];
const STEP_MS = 3600;
const PULSE_MS = 1200;
const SIZE: Size = { w: 1152, h: 416 };

const BROWSER = "#0284C7";
const BrowserGlobe = (props: { class?: string }) => (
  <TbWorld class={props.class} style={{ color: BROWSER }} aria-hidden="true" />
);

const ARROW_STEPS: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };

/**
 * The integrations as a flow through Chief, one tab per kind. Built in plays a
 * loop while on screen and on that tab: a line said in the meeting runs into
 * Chief, a comet carries it to one tool, and the tool shows what Chief did
 * there. Hovering, focusing or tapping a tool holds the loop on it and lists
 * what Chief can do there. The other tabs put their logos on the same curves;
 * a long list ends in a button that opens all of them. Narrow screens get each
 * view stacked, without the connectors.
 */
/** `labelledBy` is the id of the heading that names the tab list on the page it sits on. */
export const IntegrationHub: Component<{ labelledBy: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  const [tab, setTab] = createSignal(0);
  const [held, setHeld] = createSignal<number | null>(null);
  const [listOpen, setListOpen] = createSignal(false);
  const [size, setSize] = createSignal(SIZE);
  const clock = useBeat(scene, STEP_MS, () => held() !== null || tab() !== 0);
  // The other tabs send a comet down one curve at a time, in order.
  const pulse = useBeat(scene, PULSE_MS, () => tab() === 0 || listOpen());
  const storyAt = (beat: number) => ORDER[beat % ORDER.length];
  const story = () => storyAt(clock.beat());

  // Each id is the tab's URL fragment, so it is public: renaming one breaks its links.
  const views = (): { id: string; label: TranslationKey; logos: Logo[]; mini: Logo[] }[] => [
    {
      id: "integrations-built-in",
      label: "marketing.home.integrations.group_builtin",
      logos: BUILT_IN,
      mini: [BUILT_IN[0], BUILT_IN[3], BUILT_IN[5]],
    },
    {
      id: "integrations-meetings",
      label: "marketing.home.integrations.group_meetings",
      logos: [
        ...MEETING_APPS,
        {
          name: t("marketing.home.integrations.any_browser"),
          mark: { kind: "svg", icon: BrowserGlobe, color: BROWSER },
        },
      ],
      mini: MEETING_APPS,
    },
    { id: "integrations-mcp", label: "marketing.home.integrations.group_mcp", logos: MCP_SERVERS, mini: MCP_SERVERS },
    { id: "integrations-agents", label: "marketing.home.integrations.group_agents", logos: AGENTS, mini: AGENTS },
    {
      id: "integrations-models",
      label: "marketing.home.integrations.group_models",
      logos: MODEL_PROVIDERS,
      mini: MODEL_PROVIDERS,
    },
  ];
  const view = () => views()[tab()];
  // Past the slots, the last one becomes the button to the full list.
  const featured = () => (view().logos.length > SLOTS ? view().logos.slice(0, SLOTS - 1) : view().logos);
  const hidden = () => view().logos.slice(featured().length);
  const slots = () => (tab() === 0 ? ARC : arc(featured().length + (hidden().length > 0 ? 1 : 0)));

  const px = (point: Point) => ({ x: (point.x * size().w) / 100, y: (point.y * size().h) / 100 });
  const curve = (a: Point, b: Point) => {
    const from = px(a);
    const to = px(b);
    const bend = (to.x - from.x) * 0.5;
    return `M${from.x} ${from.y} C${from.x + bend} ${from.y}, ${to.x - bend} ${to.y}, ${to.x} ${to.y}`;
  };
  const spoke = (slot: Point) => curve(CHIEF, slot);
  // Where a slot's logo glides in from: part of the way back along its curve.
  const glide = (slot: Point, i: number) => ({
    "--fx": `${(px(CHIEF).x - px(slot).x) * 0.4}px`,
    "--fy": `${(px(CHIEF).y - px(slot).y) * 0.4}px`,
    "--delay": `${i * 45}ms`,
  });

  const pick = (index: number) => {
    setHeld(null);
    setListOpen(false);
    setTab(index);
    // Replaced, not pushed, so Back leaves the page rather than stepping through tabs.
    history.replaceState(history.state, "", tabHref(location.pathname, location.search, views()[index].id));
  };
  const onTabKey = (event: KeyboardEvent & { currentTarget: HTMLDivElement }) => {
    const step = ARROW_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = (tab() + step + views().length) % views().length;
    pick(next);
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  const release = (index: number) => {
    if (held() !== index) return;
    setHeld(null);
    const beat = clock.beat();
    clock.jump(beat - (beat % ORDER.length) + ORDER.indexOf(index) + 1);
  };
  const stateOf = (index: number) => {
    if (held() === index) return "held";
    if (held() === null && story() === index) return "story";
    return undefined;
  };
  const playing = () => scene.armed() && held() === null && tab() === 0;
  const meeting = () => (
    <MeetingNode
      said={t(BUILT_IN[storyAt(clock.beat() + ORDER.length - 1)].quote)}
      saying={t(BUILT_IN[held() ?? story()].quote)}
      beat={clock.beat()}
      animate={playing()}
    />
  );

  // Only the browser knows the fragment, so the server renders the first tab and this swaps it after hydration.
  onMount(() => {
    const index = views().findIndex((item) => `#${item.id}` === location.hash);
    if (index !== -1) pick(index);
  });

  let box!: HTMLDivElement;
  onMount(() => {
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) setSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    observer.observe(box);
    onCleanup(() => observer.disconnect());
  });

  const tools = (wide: boolean) => (
    <Index each={BUILT_IN}>
      {(item, i) => (
        <ToolTile
          item={item()}
          id={`hub-${wide ? "wide" : "narrow"}-${i}`}
          wide={wide}
          state={stateOf(i)}
          onHold={() => setHeld(i)}
          onRelease={() => release(i)}
          style={wide ? { left: `${ARC[i].x}%`, top: `${ARC[i].y}%` } : undefined}
          glide={wide ? glide(ARC[i], i) : undefined}
        />
      )}
    </Index>
  );

  return (
    <div
      role="group"
      ref={scene.ref}
      class="mk-scene relative rounded-3xl border border-primary-border bg-tile bg-[radial-gradient(var(--color-primary-border)_1px,transparent_1px)] bg-size-[22px_22px] px-4 py-5 sm:px-6 lg:px-8"
      classList={{ "mk-armed": scene.armed(), "in-view": scene.inView() }}
      style={{ "--step": `${STEP_MS}ms`, "--pulse": `${PULSE_MS}ms` }}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        setHeld(null);
        setListOpen(false);
      }}
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(ellipse_at_40%_55%,color-mix(in_srgb,var(--color-accent)_16%,transparent),transparent_55%),radial-gradient(ellipse_at_75%_55%,color-mix(in_srgb,var(--color-brand)_12%,transparent),transparent_60%)]"
      />

      <div class="group/heading relative flex items-center justify-center">
        <div
          role="tablist"
          aria-labelledby={props.labelledBy}
          class="-mx-4 flex max-w-[calc(100%+2rem)] snap-x gap-1 overflow-x-auto rounded-2xl border border-primary-border bg-panel p-1.5 shadow-inner sm:mx-0 sm:max-w-full sm:flex-wrap sm:justify-center sm:overflow-visible"
          onKeyDown={onTabKey}
        >
          <For each={views()}>
            {(item, i) => (
              <button
                type="button"
                role="tab"
                id={item.id}
                aria-selected={tab() === i()}
                aria-controls="hub-panel"
                tabIndex={tab() === i() ? 0 : -1}
                onClick={(event) => {
                  pick(i());
                  event.currentTarget.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
                }}
                class="flex shrink-0 cursor-pointer snap-start scroll-mt-28 items-center gap-2 rounded-xl border py-1.5 ps-1.5 pe-3 text-sm font-semibold whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
                classList={{
                  "border-primary-border bg-tile text-primary-text shadow-md": tab() === i(),
                  "border-transparent text-secondary-text hover:bg-tile/60 hover:text-primary-text": tab() !== i(),
                }}
              >
                <MiniStack logos={item.mini.slice(0, 3)} />
                {t(item.label)}
                <span class="font-mono text-xs font-medium text-muted-text">{item.logos.length}</span>
              </button>
            )}
          </For>
        </div>
        {/* Phones have no hover to reveal it, and the tab row runs edge to edge there. */}
        <span class="hidden shrink-0 text-2xl sm:block">
          <TabLink id={view().id} />
        </span>
      </div>

      <div role="tabpanel" id="hub-panel" aria-labelledby={view().id} class="relative mt-5">
        <div ref={box} class="relative hidden h-104 lg:block">
          <svg
            aria-hidden="true"
            viewBox={`0 0 ${size().w} ${size().h}`}
            class="absolute inset-0 size-full overflow-visible"
          >
            <defs>
              <filter id="hub-blur" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="1.5" />
              </filter>
            </defs>
            <path d={curve(MEETING, CHIEF)} fill="none" stroke-width="1" class="stroke-primary-border" />
            <Show when={tab() + 1} keyed>
              <Index each={slots()}>
                {(slot, i) => (
                  <g
                    class="mk-ink"
                    style={{ "--c": (tab() === 0 ? BUILT_IN : featured())[i]?.mark.color ?? "var(--color-accent)" }}
                  >
                    <path
                      d={spoke(slot())}
                      pathLength="100"
                      fill="none"
                      stroke-width="1"
                      class="hub-draw stroke-primary-border"
                      style={{ "--delay": `${i * 45}ms` }}
                    />
                    <Show when={tab() === 0}>
                      <path
                        d={curve(CHIEF, slot())}
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        class="hub-lit"
                        data-on={stateOf(i)}
                      />
                    </Show>
                  </g>
                )}
              </Index>
            </Show>
            <Show when={playing() && clock.beat() + 1} keyed>
              <Comet d={curve(MEETING, CHIEF)} class="hub-spark" />
              <Comet d={curve(CHIEF, ARC[story()])} class="hub-shot" />
            </Show>
            <Show when={scene.armed() && tab() !== 0 && pulse.beat() + 1} keyed>
              {(beat) => <Comet d={spoke(slots()[(beat - 1) % slots().length])} class="hub-glide" />}
            </Show>
          </svg>

          <div class="absolute -translate-1/2" style={{ left: `${MEETING.x}%`, top: `${MEETING.y}%` }}>
            {meeting()}
          </div>
          <div class="absolute -translate-1/2" style={{ left: `${CHIEF.x}%`, top: `${CHIEF.y}%` }}>
            <ChiefNode ping={playing() ? clock.beat() + 1 : 0} />
          </div>
          <Show when={tab() === 0}>
            <div class="absolute z-10 -translate-x-1/2" style={{ left: `${CHIEF.x}%`, top: "calc(50% + 4.25rem)" }}>
              <ApprovalBadge flash={playing() ? clock.beat() + 1 : 0} />
            </div>
          </Show>

          <Show when={tab() + 1} keyed>
            <Show
              when={tab() === 0}
              fallback={
                <ul aria-label={t(view().label)}>
                  <Index each={featured()}>
                    {(logo, i) => (
                      <li
                        class="absolute z-10 -translate-1/2"
                        style={{ left: `${slots()[i].x}%`, top: `${slots()[i].y}%` }}
                      >
                        <span class="hub-slide block" style={glide(slots()[i], i)}>
                          <LogoPill logo={logo()} />
                        </span>
                      </li>
                    )}
                  </Index>
                  <Show when={hidden().length > 0}>
                    <li
                      class="absolute z-10 -translate-1/2"
                      style={{ left: `${slots()[featured().length].x}%`, top: `${slots()[featured().length].y}%` }}
                    >
                      <span class="hub-slide block" style={glide(slots()[featured().length], featured().length)}>
                        <MoreButton logos={hidden()} open={listOpen()} onClick={() => setListOpen(true)} />
                      </span>
                    </li>
                  </Show>
                </ul>
              }
            >
              <ul aria-label={t(view().label)}>{tools(true)}</ul>
            </Show>
          </Show>
          <Show when={listOpen()}>
            <LogoList label={t(view().label)} logos={view().logos} onClose={() => setListOpen(false)} />
          </Show>
        </div>

        <div class="flex flex-col items-center gap-3 lg:hidden">
          {meeting()}
          <span aria-hidden="true" class="h-5 w-px bg-accent/60" />
          <ChiefNode ping={playing() ? clock.beat() + 1 : 0} />
          <span aria-hidden="true" class="h-5 w-px bg-accent/60" />
          <Show
            when={tab() === 0}
            fallback={
              <ul aria-label={t(view().label)} class="flex flex-wrap justify-center gap-2">
                <For each={listOpen() ? view().logos : featured()}>
                  {(logo) => (
                    <li>
                      <LogoPill logo={logo} />
                    </li>
                  )}
                </For>
                <Show when={hidden().length > 0}>
                  <li>
                    <Show
                      when={listOpen()}
                      fallback={<MoreButton logos={hidden()} open={false} onClick={() => setListOpen(true)} />}
                    >
                      <button
                        type="button"
                        aria-expanded="true"
                        onClick={() => setListOpen(false)}
                        class="h-10 cursor-pointer rounded-full border border-primary-border bg-tile px-3.5 text-sm font-medium text-secondary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
                      >
                        {t("marketing.home.integrations.show_less")}
                      </button>
                    </Show>
                  </li>
                </Show>
              </ul>
            }
          >
            <ul aria-label={t(view().label)} class="flex w-full max-w-md flex-col gap-2">
              {tools(false)}
            </ul>
            <ApprovalBadge flash={playing() ? clock.beat() + 1 : 0} />
          </Show>
        </div>
      </div>
    </div>
  );
};

/**
 * A glowing dot with a fading tail that runs along `d`: three dashes of
 * decreasing length stacked head to head, the longest blurred into a halo.
 */
const Comet = (props: { d: string; class: string }) => (
  <g class={`hub-comet text-accent ${props.class}`}>
    <path
      d={props.d}
      pathLength="100"
      stroke-width="5"
      filter="url(#hub-blur)"
      opacity="0.3"
      style={{ "--len": "6px" }}
    />
    <path d={props.d} pathLength="100" stroke-width="1.5" opacity="0.35" style={{ "--len": "11px" }} />
    <path d={props.d} pathLength="100" stroke-width="2" class="hub-head" opacity="0.9" style={{ "--len": "3px" }} />
  </g>
);

/** The guarantee under Chief; it lights each time the story lands a result. */
const ApprovalBadge = (props: { flash: number }) => {
  const { t } = useI18n();
  return (
    <p class="relative flex items-center gap-1.5 rounded-full bg-tile bg-linear-to-r from-accent/10 to-accent/10 py-1 ps-2 pe-3 text-xs font-medium whitespace-nowrap text-primary-text shadow-sm ring-1 ring-accent/30 dark:from-accent/15 dark:to-accent/15">
      <Show when={props.flash} keyed>
        <span aria-hidden="true" class="hub-approve absolute -inset-px rounded-full ring-2 ring-accent" />
      </Show>
      <TbShieldCheck class="size-4 text-accent" aria-hidden="true" />
      {t("marketing.home.integrations.approval")}
    </p>
  );
};

const MiniStack = (props: { logos: Logo[] }) => (
  <span aria-hidden="true" class="flex -space-x-1.5">
    <For each={props.logos}>
      {(logo) => (
        <span
          class="grid size-6 place-items-center rounded-full bg-tile ring-2 ring-tile"
          style={{ "--c": logo.mark.color }}
        >
          <span class="grid size-full place-items-center rounded-full bg-(--c)/12 dark:bg-(--c)/20">
            <LogoMark mark={logo.mark} class="size-3.5" />
          </span>
        </span>
      )}
    </For>
  </span>
);

const MoreButton = (props: { logos: Logo[]; open: boolean; onClick: () => void }) => {
  const { t } = useI18n();
  return (
    <button
      type="button"
      aria-expanded={props.open}
      onClick={() => props.onClick()}
      class="flex h-10 cursor-pointer items-center gap-2 rounded-full border border-accent/50 bg-tile py-1.5 ps-1.5 pe-3.5 text-sm font-semibold whitespace-nowrap text-primary-text shadow-sm transition-colors hover:border-accent hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
    >
      <MiniStack logos={props.logos.slice(0, 4)} />
      {t("marketing.home.integrations.more", { count: props.logos.length })}
    </button>
  );
};

/** Every logo of a tab by name, over the stage, for the ones its slots have no room for. */
const LogoList = (props: { label: string; logos: Logo[]; onClose: () => void }) => {
  const { t } = useI18n();
  let close!: HTMLButtonElement;
  onMount(() => {
    const opener = document.activeElement;
    close.focus();
    onCleanup(() => opener instanceof HTMLElement && opener.focus());
  });
  return (
    <div
      role="dialog"
      aria-label={props.label}
      class="hub-in absolute inset-x-[8%] inset-y-2 z-30 flex flex-col rounded-2xl border border-primary-border bg-tile/95 p-5 shadow-xl backdrop-blur-sm"
    >
      <div class="flex items-center justify-between gap-4">
        <p class="font-mono text-xs font-semibold tracking-[0.18em] text-muted-text uppercase">
          {props.label} · {props.logos.length}
        </p>
        <button
          ref={close}
          type="button"
          aria-label={t("marketing.home.integrations.show_less")}
          onClick={() => props.onClose()}
          class="grid size-8 cursor-pointer place-items-center rounded-full text-secondary-text hover:bg-panel hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          <TbX class="size-4" aria-hidden="true" />
        </button>
      </div>
      <ul class="mt-4 flex flex-wrap content-start gap-2 overflow-y-auto pb-1">
        <For each={props.logos}>
          {(logo) => (
            <li>
              <LogoPill logo={logo} />
            </li>
          )}
        </For>
      </ul>
    </div>
  );
};

/** The meeting: the line said before, muted, and the one Chief is acting on now. */
const MeetingNode = (props: { said: string; saying: string; beat: number; animate: boolean }) => {
  const { t } = useI18n();
  return (
    <div class="flex w-56 flex-col gap-2 rounded-2xl border border-primary-border bg-tile p-3 shadow-sm">
      <span class="flex items-center gap-2">
        <BrandGlyph src="/images/logos/zoom.svg" class="size-4.5 text-logo-zoom" />
        <BrandGlyph src="/images/logos/googlemeet.svg" class="size-4.5 text-logo-meet" />
        <BrandGlyph src="/images/logos/microsoftteams.svg" class="size-4.5 text-logo-teams" />
        <span class="ms-auto flex items-center gap-1.5 font-mono text-[11px] font-semibold tracking-wider text-secondary-text uppercase">
          <span aria-hidden="true" class="size-1.5 rounded-full bg-accent motion-safe:animate-pulse" />
          {t("marketing.home.integrations.hub_meeting")}
        </span>
      </span>
      <Show when={props.beat + 1} keyed>
        <q class="block px-1 text-xs leading-snug text-muted-text" classList={{ "hub-quote": props.animate }}>
          {props.said}
        </q>
        <q
          class="block rounded-xl rounded-tl-sm bg-accent/10 px-2.5 py-1.5 text-xs leading-snug text-primary-text ring-1 ring-accent/20"
          classList={{ "hub-quote": props.animate }}
        >
          {props.saying}
        </q>
      </Show>
    </div>
  );
};

const ChiefNode = (props: { ping: number }) => (
  <div class="relative z-10 grid size-24 place-items-center rounded-3xl border border-accent/40 bg-tile shadow-[0_0_60px_-8px_var(--color-accent)] ring-8 ring-accent/10">
    <Show when={props.ping} keyed>
      <span aria-hidden="true" class="hub-ping absolute -inset-1 rounded-[1.75rem] ring-2 ring-accent" />
    </Show>
    <img src="/images/chief-logo-gold-crown-only.svg" alt="Chief" class="h-9 w-auto" />
  </div>
);

const ToolTile = (props: {
  item: (typeof BUILT_IN)[number];
  id: string;
  wide: boolean;
  state: "story" | "held" | undefined;
  onHold: () => void;
  onRelease: () => void;
  style?: { left: string; top: string };
  glide?: Record<string, string>;
}) => {
  const { t } = useI18n();
  let wasHeld = false;
  let pointer = "mouse";
  return (
    <li
      classList={{
        "absolute -translate-1/2": props.wide,
        relative: !props.wide,
        "z-20": props.state !== undefined,
        "z-10": props.state === undefined,
      }}
      style={{ ...props.style, "--brand": props.item.mark.color, "--c": props.item.mark.color }}
    >
      <div class="relative" classList={{ "hub-slide": props.wide }} style={props.glide}>
        <button
          type="button"
          aria-describedby={props.id}
          data-on={props.state}
          onPointerDown={(event) => {
            pointer = event.pointerType;
            wasHeld = props.state === "held";
          }}
          onPointerEnter={(event) => event.pointerType === "mouse" && props.onHold()}
          onPointerLeave={(event) => event.pointerType === "mouse" && props.onRelease()}
          onFocus={() => props.onHold()}
          onBlur={() => props.onRelease()}
          onClick={() => {
            if (pointer === "mouse") return;
            if (wasHeld) props.onRelease();
            else props.onHold();
          }}
          class="hub-tool relative flex cursor-pointer items-center gap-2.5 rounded-2xl border border-primary-border bg-tile bg-linear-to-br from-(--brand)/10 to-transparent py-2 ps-2 pe-3.5 text-start whitespace-nowrap shadow-sm transition-[translate,border-color] duration-300 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
          classList={{ "min-w-36": props.wide, "w-full": !props.wide }}
        >
          <span
            aria-hidden="true"
            class="hub-glow pointer-events-none absolute -inset-px rounded-2xl shadow-[0_10px_30px_-10px_var(--brand)] ring-1 ring-(--brand)/45 dark:ring-(--brand)/60"
          />
          <span class="grid size-7 shrink-0 place-items-center rounded-xl bg-tile shadow-sm">
            <LogoMark mark={props.item.mark} class="size-4.5" />
          </span>
          <span class="text-sm leading-tight font-medium">{props.item.name}</span>
          <Show when={!props.wide && props.state === "story"}>
            <ResultChip text={t(props.item.result)} class="ms-auto" />
          </Show>
        </button>
        <Show when={props.wide && props.state === "story"}>
          <ResultChip text={t(props.item.result)} class="absolute inset-s-full top-1/2 ms-3 -translate-y-1/2" />
        </Show>
        <span
          id={props.id}
          role="tooltip"
          class="pointer-events-none absolute z-10 rounded-xl bg-primary-text px-3 py-2 text-xs leading-snug text-inverted-text shadow-lg transition-opacity duration-200"
          classList={{
            "start-full top-1/2 ms-3 w-52 -translate-y-1/2": props.wide,
            "inset-x-0 top-full mt-2": !props.wide,
            "invisible opacity-0": props.state !== "held",
          }}
        >
          <span class="font-semibold">{props.item.name}</span>
          <span class="mt-0.5 block text-inverted-text/80">{t(props.item.verbs)}</span>
        </span>
      </div>
    </li>
  );
};

const ResultChip = (props: { text: string; class: string }) => (
  <span
    class={`hub-chip mk-ink flex items-center gap-1.5 rounded-full bg-tile bg-linear-to-r from-(--c)/12 to-(--c)/12 py-1 ps-1.5 pe-2.5 text-xs font-semibold whitespace-nowrap shadow-sm ring-1 ring-(--c)/25 dark:from-(--c)/20 dark:to-(--c)/20 ${props.class}`}
  >
    <span class="grid size-4 place-items-center rounded-full bg-(--c)/15">
      <TbCheck class="size-3" aria-hidden="true" />
    </span>
    {props.text}
  </span>
);
